import {
    FGSidebarNav,
    FGIcons,
    FGTypography,
    FGSidebarNavFooterLinkDivider,
    FGSidebarNavLink,
    FGSidebarAnchorComponentProps,
    FGSidebarNavLinksWrapper,
    FGSidebarNavFooterLinksWrapper,
    FGTooltip,
    TOOLTIP_PLACEMENT,
} from '@fg/design-system';
import { DEFAULT_VIEWS, VIEW } from 'interfaces';
import { useEffect, useRef, useState } from 'react';
import { useResolvedPath, useMatch, Link } from 'react-router-dom';
import cx from 'classnames';
import { CROSS_COUNTRY_VIEWS } from 'components/Pickers/CrossCountryRiskPicker';
import { useCurrentView } from 'hooks/useCurrentView';
import { includes } from 'lodash/fp';
import { MULTILATERAL_VIEWS } from 'components/Pickers/MultilateralRiskPicker';
import { METHODOLOGIES_PDF_URL } from 'components/Homepage/Methodologies';
import { inject } from 'mobx-react';
import rootStore, { IMobxRootState } from 'RootStore';
import useIsGQInternalUser from 'hooks/useIsGQInternalUser';
import { PAGE_TITLES } from 'PageTitles';

const CROSS_COUNTRY_VIEWS_PATHS = CROSS_COUNTRY_VIEWS.map(item => item.path);
const MULTILATERAL_VIEWS_PATHS = MULTILATERAL_VIEWS.map(item => item.path);

type Props = {
    defaultEvent?: number;
};

export const Sidebar = inject(({ eventsFeedStore }: IMobxRootState) => {
    return {
        defaultEvent: eventsFeedStore.defaultEvent,
    };
})(({ defaultEvent }: Props) => {
    const isGQInternalUser = useIsGQInternalUser();
    const isHomepage = useCurrentView() === DEFAULT_VIEWS.HOME;
    const [isExpanded, setIsExpanded] = useState(isHomepage);
    const [initialMode, setSidebarInitialMode] = useState<
        'embedded' | 'collapsed'
    >(isHomepage ? 'embedded' : 'collapsed');
    const didLeaveHomepage = useRef(!isHomepage);
    useEffect(() => {
        if (!isHomepage && !didLeaveHomepage.current) {
            didLeaveHomepage.current = true;
            setSidebarInitialMode('collapsed');
            setIsExpanded(false);
        }
    }, [isHomepage]);
    const onExpandToggle = (isExpanded: boolean) => {
        setIsExpanded(isExpanded);
        setTimeout(() => {
            rootStore.chartStore.updateDimensions();
        }, 300);
    }
    const CategoryLabelOrDivider = ({ label }: { label: string }) => {
        if (isExpanded) {
            return (
                <div style={{ paddingBlock: 8 }}>
                    <FGTypography
                        variant="body3-short"
                        color="weak"
                        tag="label">
                        {label}
                    </FGTypography>
                </div>
            );
        }
        return <FGSidebarNavFooterLinkDivider />;
    };
    const ReactRouterAnchorComponent = ({
        children,
        activeClassName,
        className,
        item,
        ...props
    }: FGSidebarAnchorComponentProps) => {
        const resolved = useResolvedPath(props.to);
        let match = useMatch({
            path: resolved.pathname,
            end: false,
        });
        const riskIntelligenceMatch = useMatch({
            path: `/${VIEW.RISK_INTELLIGENCE}`,
            end: false,
        });
        const scoreEventMatch = useMatch({
            path: `/${VIEW.SCORE_EVENT}`,
            end: false,
        });
        const subView = useCurrentView();
        //todo: fix by making all sub views have the parent path as prefix
        // eg: '/cross_country/world_graph'
        const isActive = (() => {
            const to = typeof props.to === 'string' ? props.to : '';
            switch (true) {
                case to === VIEW.WORLD_GRAPH:
                    return includes(subView, CROSS_COUNTRY_VIEWS_PATHS);
                case to === VIEW.CONTINGENT_GRAPH:
                    return includes(subView, MULTILATERAL_VIEWS_PATHS);
                case to === `${VIEW.RISK_INTELLIGENCE}/insights`:
                case to === `${VIEW.RISK_INTELLIGENCE}/insights/`:
                    return Boolean(riskIntelligenceMatch);
                case to.startsWith(`/${VIEW.SCORE_EVENT}`) ||
                    to.startsWith(VIEW.SCORE_EVENT):
                    return Boolean(scoreEventMatch);
                default:
                    return match;
            }
        })();
        //todo: refactor - DRY Link composition
        if (isExpanded) {
            return (
                <FGTypography variant="label3" color="strong" tag="label">
                    <Link
                        className={cx(className, isActive && activeClassName)}
                        onClick={() => {}}
                        {...props}>
                        {children}
                    </Link>
                </FGTypography>
            );
        }
        return (
            <FGTooltip
                placement={TOOLTIP_PLACEMENT.right}
                appendTo={'parent'}
                content={
                    <FGTypography variant="label2" color="strong" tag="label">
                        {item.text}
                    </FGTypography>
                }>
                <Link
                    className={cx(className, isActive && activeClassName)}
                    onClick={() => {}}
                    {...props}>
                    {children}
                </Link>
            </FGTooltip>
        );
    };
    const scoreEventUrl = defaultEvent
        ? `/${VIEW.SCORE_EVENT}/${defaultEvent}`
        : `/${VIEW.SCORE_EVENT}`;
    return (
        <div className="left-nav-container">
            <FGSidebarNav
                key={initialMode}
                onExpandToggle={onExpandToggle}
                anchorComponent={ReactRouterAnchorComponent}
                pinnedMode
                initialMode={initialMode}
                itemsContent={
                    <FGSidebarNavLinksWrapper>
                        <div>
                            <FGSidebarNavLink
                                href={`${DEFAULT_VIEWS.HOME}`}
                                icon={<FGIcons icon="Home" />}
                                id={DEFAULT_VIEWS.HOME}
                                text={PAGE_TITLES.HOME}
                            />
                            <CategoryLabelOrDivider label="Analytics" />
                            <FGSidebarNavLink
                                href={`${VIEW.COUNTRY_GRAPH}`}
                                icon={<FGIcons icon="Flag2" />}
                                id={VIEW.COUNTRY_GRAPH}
                                text={PAGE_TITLES.SINGLE_COUNTRY_RISKS}
                            />
                            <FGSidebarNavLink
                                href={`${VIEW.WORLD_GRAPH}`}
                                icon={<FGIcons icon="Globe" />}
                                id={VIEW.WORLD_GRAPH}
                                text={PAGE_TITLES.MULTI_COUNTRY_RISKS}
                            />
                            <FGSidebarNavLink
                                href={`${VIEW.CONTINGENT_GRAPH}`}
                                icon={<FGIcons icon="Nodes" />}
                                id={VIEW.CONTINGENT_GRAPH}
                                text={PAGE_TITLES.BILATERAL_RISKS}
                            />
                            <CategoryLabelOrDivider label="Insights" />
                            {!isGQInternalUser ? (
                                <FGSidebarNavLink
                                    href={`${VIEW.RISK_INTELLIGENCE}/insights`}
                                    icon={<FGIcons icon="Document" />}
                                    id={VIEW.RISK_INTELLIGENCE}
                                    text={PAGE_TITLES.ANALYSIS_AND_PREDICTIONS}
                                />
                            ) : (
                                <>
                                    <FGSidebarNavLink
                                        href={`${VIEW.RISK_INTELLIGENCE}/insights/`}
                                        icon={<FGIcons icon="Document" />}
                                        id={VIEW.RISK_INTELLIGENCE}
                                        text={PAGE_TITLES.ANALYSIS_AND_PREDICTIONS}
                                    />
                                    <FGSidebarNavLink
                                        href={`${VIEW.CREATE_INSIGHT}/${VIEW.RISK_VIEW}`}
                                        icon={<FGIcons icon="BookmarkPlus" />}
                                        id={VIEW.CREATE_INSIGHT}
                                        text={PAGE_TITLES.CREATE_INSIGHT}
                                    />
                                    <FGSidebarNavLink
                                        href={scoreEventUrl}
                                        icon={<FGIcons icon="Gauge" />}
                                        id={VIEW.SCORE_EVENT}
                                        text={PAGE_TITLES.EVENT_SCORING}
                                    />
                                </>
                            )}
                        </div>
                        <FGSidebarNavFooterLinksWrapper>
                            <div
                                className="left-nav-methodologies"
                                onClickCapture={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    window.open(
                                        METHODOLOGIES_PDF_URL,
                                        '_blank'
                                    );
                                }}>
                                <FGSidebarNavLink
                                    href={`${VIEW.METHODOLOGIES}`}
                                    icon={<FGIcons icon="Info" />}
                                    id={VIEW.METHODOLOGIES}
                                    text={PAGE_TITLES.METHODOLOGIES}
                                />
                            </div>
                        </FGSidebarNavFooterLinksWrapper>
                    </FGSidebarNavLinksWrapper>
                }
            />
        </div>
    );
});

export default Sidebar;
